import type { ReportDraft } from './report-draft';

/**
 * Minimal structural stand-in for a Firestore `Timestamp` so this canonical
 * survey document model stays framework-free (no `firebase/firestore` import),
 * which is what lets it live in `@officehub/shared` and be consumed by both the
 * CRA client and the Vite Sales PWA. A real client-SDK or admin-SDK `Timestamp`
 * satisfies this shape structurally, so values read from Firestore assign to
 * these fields unchanged and `.toDate()` keeps working. Read boundaries that
 * need an ISO string should route through `toIsoString` in
 * `../lib/survey/normalize`.
 */
export interface FirestoreTimestampLike {
  toDate(): Date;
  toMillis(): number;
  readonly seconds: number;
  readonly nanoseconds: number;
}

// === Status Enum ===

export const SURVEY_STATUS = {
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  REPORT_GENERATED: 'report_generated',
  ARCHIVED: 'archived',
} as const;

export type SurveyStatus = (typeof SURVEY_STATUS)[keyof typeof SURVEY_STATUS];

// `normalizeSurveyStatus` lives in `../lib/survey/normalize` (framework-free) so
// both apps can import it without pulling this module's type imports.

export type CaptureMode = 'field' | 'interview' | 'hybrid' | 'quick';

export type SurveySection =
  | 'siteInfo'
  | 'electricalPanel'
  | 'gasMeter'
  | 'generatorPad'
  | 'loads'
  | 'existingEquipment'
  | 'permit'
  | 'proposal'
  | 'siteMap';

export const SURVEY_SECTIONS: SurveySection[] = [
  'siteInfo',
  'electricalPanel',
  'gasMeter',
  'generatorPad',
  'loads',
  'existingEquipment',
  'permit',
  'proposal',
  'siteMap',
];

/** Section type for photos — excludes 'siteMap' which uses its own snapshot mechanism */
// 'permit' and 'proposal' hold typed answers only: survey_addPhoto (surveyPhotoService) rejects them, so they are not photo sections.
export type PhotoSection = Exclude<SurveySection, 'siteMap' | 'permit' | 'proposal'>;

export type PhotoTag =
  | 'panel_label'
  | 'panel_interior'
  | 'gas_meter'
  | 'proposed_pad'
  | 'existing_equipment'
  | 'site_overview'
  | 'other';

// === Photo ===

/** A photo captured during a site survey, tagged with its section and semantic type */
export interface SurveyPhoto {
  photoId: string;
  url: string;
  storageRef: string;
  section: PhotoSection;
  tag: PhotoTag;
  caption?: string;
  analysisResult?: Record<string, unknown>;
  capturedAt: FirestoreTimestampLike;
  capturedBy: string;
}

// === Section Interfaces ===

/** Site-level information: address, terrain, restrictions */
export interface SiteInfoSection {
  address?: string;
  coordinates?: { lat: number; lng: number };
  accessNotes?: string;
  soilType?: 'clay' | 'sand' | 'rock' | 'fill' | 'unknown';
  grade?: 'flat' | 'slight_slope' | 'steep' | 'terraced';
  noiseRestrictions?: string;
  hoaRestrictions?: string;
  ahjNotes?: string;
  // Written by the AI interview extraction (functions-ai extractSurveyFromInterview),
  // which stores an explicit null when it found no value.
  propertyType?: 'residential' | 'commercial' | 'industrial' | null;
  squareFootage?: number | null;
  numStories?: number | null;
  preferredPlacementNotes?: string | null;
}

/** Electrical panel data: brand, amps, spaces, distance to pad */
export interface ElectricalPanelSection {
  brand?: string;
  mainBreakerAmps?: number;
  busRating?: number;
  totalSpaces?: number;
  availableSpaces?: number;
  panelLocation?: string;
  distanceToGeneratorPad?: number;
  existingGroundingInfo?: string;
  /** Written by the AI interview extraction; null when it found no value. */
  hasSubPanels?: boolean | null;
  photos: SurveyPhoto[];
}

/** Gas meter data: location, capacity, distance to pad */
export interface GasMeterSection {
  meterLocation?: string;
  meterCapacityBTU?: number;
  existingPressure?: string;
  distanceToGeneratorPad?: number;
  pipeRoutingNotes?: string;
  /** Written by the AI interview extraction; null when it found no value. */
  hasNaturalGas?: boolean | null;
  /** Read off the meter by AI photo analysis; rendered into the site-visit report. */
  meterNumber?: string;
  photos: SurveyPhoto[];
}

/** Proposed generator pad: location, dimensions, access, clearances */
export interface GeneratorPadSection {
  proposedLocation?: string;
  padExists: boolean;
  /** null = not recorded (older surveys may carry 0 for the same thing; readers treat 0 as not recorded). */
  padDimensions?: { length: number | null; width: number | null };
  craneAccess: boolean;
  craneAccessNotes?: string;
  clearances?: { front: number | null; back: number | null; left: number | null; right: number | null };
  exhaustRoutingNotes?: string;
  photos: SurveyPhoto[];
}

/** Load analysis: transfer type, critical loads, estimated kW */
export interface LoadsSection {
  transferType?: 'whole_house' | 'partial' | 'critical_only';
  criticalLoads?: string[];
  estimatedTotalLoadKW?: number;
  customerPriorities?: string;
  futureExpansionNotes?: string;
}

/** Whether the gas service must change for the generator (Plumbing permit box; utility application). */
export const PERMIT_GAS_SERVICE_CHANGES = ['none', 'meter_upgrade', 'new_service'] as const;
export type PermitGasServiceChange = (typeof PERMIT_GAS_SERVICE_CHANGES)[number];

/** How the house's waste water leaves the parcel (application "septic or sewer"). */
export const PERMIT_SEPTIC_OR_SEWER = ['septic', 'sewer'] as const;
export type PermitSepticOrSewer = (typeof PERMIT_SEPTIC_OR_SEWER)[number];

/**
 * Site facts a county permit asks for that only someone on site can answer
 * (docs/plans/permit-package/site-survey-permit-fields.md section 2). Every
 * field is optional: blank means "not asked yet", never "no". Value rules live
 * in shared/src/lib/survey/permitSection.ts and are enforced by the server.
 */
export interface PermitSection {
  /** Smoke alarms in the house (Monterey smoke/CO affidavit). */
  smokeAlarmCount?: number;
  /** Carbon-monoxide alarms in the house (same affidavit). */
  coAlarmCount?: number;
  /** The building has fire sprinklers or a monitored alarm (fire question). */
  sprinklersOrMonitoredAlarm?: boolean;
  /** Trees must come out for the unit or the trench (planning question; may need its own permit). */
  treesRemoved?: boolean;
  gasServiceChange?: PermitGasServiceChange;
  septicOrSewer?: PermitSepticOrSewer;
  /** A well on the parcel (application question). */
  wellOnParcel?: boolean;
  /** An open planning permit, conditions on a related permit, or an HOA that must approve. */
  openPlanningPermitsOrHoa?: boolean;
  openPlanningPermitsOrHoaNote?: string;
}

/** The fuel of the generator we propose. One vocabulary for the survey, the lead and the permit kit. */
export const SURVEY_FUELS = ['natural_gas', 'propane', 'diesel'] as const;
export type SurveyFuel = (typeof SURVEY_FUELS)[number];

/** What happens to an existing system: left as it is, taken out for a new one, or there was none. */
export const SYSTEM_CHANGES = ['keep', 'replace', 'new'] as const;
export type SystemChange = (typeof SYSTEM_CHANGES)[number];

/** Propane: is the tank already on site, or do we bring a new one. */
export const PROPANE_TANK_STATUSES = ['existing', 'new'] as const;
export type PropaneTankStatus = (typeof PROPANE_TANK_STATUSES)[number];

/** Diesel: the kind of tank. Usually the unit mounts on top of a sub-base tank. */
export const DIESEL_TANK_KINDS = ['sub_base', 'above_ground', 'underground'] as const;
export type DieselTankKind = (typeof DIESEL_TANK_KINDS)[number];

/**
 * What we propose, and what changes versus what stays, for the four systems the
 * proposal, the permit and billing all read: fuel supply, electrical panel,
 * transfer switch and generator pad. The existing side of each lives in its own
 * section (gasMeter, electricalPanel, generatorPad); this section holds the
 * decision. Every field is flat so a partial save MERGES by field path and never
 * drops a sibling (the server merge is one level deep). Blank means "not asked
 * yet", never "keep". A survey with no proposedFuel reads as natural gas.
 */
export interface ProposalSection {
  proposedFuel?: SurveyFuel;
  /** Fuel supply: the gas service (natural gas), the tank (propane or diesel). */
  fuelSupplyChange?: SystemChange;
  propaneTankStatus?: PropaneTankStatus;
  /** Where the propane tank goes (or already sits). */
  propaneTankLocation?: string;
  dieselTankKind?: DieselTankKind;
  dieselTankLocation?: string;
  panelChange?: SystemChange;
  panelChangeNotes?: string;
  /** What transfer switch is there today, in the surveyor's words. */
  transferSwitchExisting?: string;
  transferSwitchChange?: SystemChange;
  /** What transfer switch we propose, in the surveyor's words. */
  transferSwitchProposed?: string;
  padChange?: SystemChange;
}

/** Existing generator info and disposal needs */
export interface ExistingEquipmentSection {
  hasExistingGenerator: boolean;
  existingGeneratorInfo?: string;
  disposalNeeded: boolean;
  photos: SurveyPhoto[];
}

// === Client-Writable Section Fields ===

/** Every writable key of a section — everything except the server-owned photo array. */
type WritableSectionKeys<T> = Exclude<keyof T, 'photos'>;

/** The writable keys a field list failed to name, or `never` when it is complete. */
type MissingSectionFields<T, L extends readonly PropertyKey[]> = Exclude<
  WritableSectionKeys<T>,
  L[number]
>;

/**
 * `true` when `L` names every writable key of `T`, otherwise the missing keys
 * themselves — so a stale list cannot compile and the error names the field.
 */
type SectionFieldsComplete<T, L extends readonly PropertyKey[]> = [
  MissingSectionFields<T, L>,
] extends [never]
  ? true
  : MissingSectionFields<T, L>;

/** Rejects a listed name that is not actually a key of its section. */
type SectionFieldLists = {
  siteInfo: readonly WritableSectionKeys<SiteInfoSection>[];
  electricalPanel: readonly WritableSectionKeys<ElectricalPanelSection>[];
  gasMeter: readonly WritableSectionKeys<GasMeterSection>[];
  generatorPad: readonly WritableSectionKeys<GeneratorPadSection>[];
  loads: readonly WritableSectionKeys<LoadsSection>[];
  existingEquipment: readonly WritableSectionKeys<ExistingEquipmentSection>[];
  permit: readonly WritableSectionKeys<PermitSection>[];
  proposal: readonly WritableSectionKeys<ProposalSection>[];
};

/**
 * The field names `survey_updateSection` may write, per section.
 *
 * The callable writes one `${section}.${field}` Firestore dot-path per supplied
 * field so that a partial edit MERGES instead of replacing the section map
 * (issue #3230). That makes these lists a security boundary as well as a
 * schema: they are what stops a raw client key from reaching a field path.
 *
 * `photos` is excluded from every section — photo arrays are server-owned and
 * only ever mutated by survey_addPhoto / survey_removePhoto. `siteMap` is
 * absent entirely: it belongs to survey_saveMap, which enforces the item-count
 * and payload-size caps that keep an oversized write from bricking the doc.
 */
export const SURVEY_SECTION_FIELDS = {
  siteInfo: [
    'address',
    'coordinates',
    'accessNotes',
    'soilType',
    'grade',
    'noiseRestrictions',
    'hoaRestrictions',
    'ahjNotes',
    'propertyType',
    'squareFootage',
    'numStories',
    'preferredPlacementNotes',
  ],
  electricalPanel: [
    'brand',
    'mainBreakerAmps',
    'busRating',
    'totalSpaces',
    'availableSpaces',
    'panelLocation',
    'distanceToGeneratorPad',
    'existingGroundingInfo',
    'hasSubPanels',
  ],
  gasMeter: [
    'meterLocation',
    'meterCapacityBTU',
    'existingPressure',
    'distanceToGeneratorPad',
    'pipeRoutingNotes',
    'hasNaturalGas',
    'meterNumber',
  ],
  generatorPad: [
    'proposedLocation',
    'padExists',
    'padDimensions',
    'craneAccess',
    'craneAccessNotes',
    'clearances',
    'exhaustRoutingNotes',
  ],
  loads: [
    'transferType',
    'criticalLoads',
    'estimatedTotalLoadKW',
    'customerPriorities',
    'futureExpansionNotes',
  ],
  existingEquipment: ['hasExistingGenerator', 'existingGeneratorInfo', 'disposalNeeded'],
  permit: [
    'smokeAlarmCount',
    'coAlarmCount',
    'sprinklersOrMonitoredAlarm',
    'treesRemoved',
    'gasServiceChange',
    'septicOrSewer',
    'wellOnParcel',
    'openPlanningPermitsOrHoa',
    'openPlanningPermitsOrHoaNote',
  ],
  proposal: [
    'proposedFuel',
    'fuelSupplyChange',
    'propaneTankStatus',
    'propaneTankLocation',
    'dieselTankKind',
    'dieselTankLocation',
    'panelChange',
    'panelChangeNotes',
    'transferSwitchExisting',
    'transferSwitchChange',
    'transferSwitchProposed',
    'padChange',
  ],
} as const satisfies SectionFieldLists;

/** A section whose fields the client may write through survey_updateSection. */
export type WritableSurveySection = keyof typeof SURVEY_SECTION_FIELDS;

/**
 * Compile-time proof that each list above is complete.
 *
 * Add a field to one of the section interfaces without listing it in
 * SURVEY_SECTION_FIELDS and this fails to compile, naming the section and the
 * missing field. Add a whole new section to SURVEY_SECTION_FIELDS and this
 * fails too, because the mapped type then demands an entry for it. That is
 * deliberate: an unlisted field is one the server will not write, and a field
 * that silently stops saving is exactly the class of defect issue #3230 was.
 *
 * Section shapes are read off `SiteSurvey` rather than re-listed here, so this
 * check has no hand-maintained list of its own to drift.
 */
export const SURVEY_SECTION_FIELDS_ARE_COMPLETE: {
  // Keyed off SurveySection, NOT off SURVEY_SECTION_FIELDS. Keying it off the
  // list would only check sections someone already remembered to add; keying it
  // off the model makes ADDING A SECTION and forgetting the list a build error
  // too, which is the drift direction that actually happens. `siteMap` is
  // excluded because survey_saveMap owns it.
  [K in Exclude<SurveySection, 'siteMap'>]: SectionFieldsComplete<
    NonNullable<SiteSurvey[K]>,
    (typeof SURVEY_SECTION_FIELDS)[K]
  >;
} = {
  siteInfo: true,
  electricalPanel: true,
  gasMeter: true,
  generatorPad: true,
  loads: true,
  existingEquipment: true,
  permit: true,
  proposal: true,
};

// === AI Outputs ===

/** AI-generated equipment recommendation with rule-based + AI layers */
export interface SurveyRecommendation {
  suggestedGeneratorKW?: number;
  suggestedFuelType?: 'natural_gas' | 'lp' | 'diesel' | 'bi_fuel';
  suggestedPhase?: '1' | '3';
  catalogMatchIds?: string[];
  reasoning?: string;
  necAmpacitySuggestion?: number;
  suggestedATSAmps?: number;
  confidenceLevel?: 'high' | 'medium' | 'low';
  warnings?: string[];
}

/** Results from AI photo analysis (panel labels, meter readings) */
export interface PhotoAnalysisResult {
  panelReadings?: {
    brand?: string;
    mainBreakerAmps?: number;
    busRating?: number;
    totalSpaces?: number;
    availableSpaces?: number;
  };
  meterReadings?: {
    capacityBTU?: number;
    pressure?: string;
    meterNumber?: string;
  };
  analysisStatus: 'pending' | 'complete' | 'partial' | 'failed';
  rawAnalysisResults?: Record<string, unknown>;
}

// === Quick Mobile Capture (AI Intake) ===

/** GPS location captured when a mobile survey is started */
export interface CaptureLocation {
  lat: number;
  lng: number;
  accuracy?: number;
  /** ISO 8601 timestamp of when the location was captured */
  capturedAt: string;
}

/** Reverse-geocoded address derived from the capture location */
export interface ResolvedAddress {
  formatted?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  lat?: number;
  lng?: number;
}

/** AI classification status for a dumped intake photo */
export type IntakePhotoStatus = 'classifying' | 'classified' | 'failed';

/** Category the AI assigns to a dumped photo */
export type IntakePhotoCategory =
  | 'electrical_panel'
  | 'gas_meter'
  | 'generator_pad'
  | 'existing_equipment'
  | 'site_overview'
  | 'business_card'
  | 'badge'
  | 'other';

/**
 * A photo dumped into the mobile survey. Uploaded immediately with status
 * 'classifying'; the AI fills in category/description/extracted data after.
 */
export interface IntakePhoto {
  photoId: string;
  url: string;
  storageRef: string;
  status: IntakePhotoStatus;
  category?: IntakePhotoCategory;
  /** Maps to the legacy PhotoTag taxonomy when applicable */
  tag?: PhotoTag;
  /** One-line human-readable description of what the AI sees */
  description?: string;
  /** Structured fields extracted from the photo (panel readings, meter, etc.) */
  extracted?: Record<string, unknown>;
  confidence?: 'high' | 'medium' | 'low';
  /** ISO 8601 timestamp */
  capturedAt: string;
  capturedBy: string;

  // --- Annotation (Phase 2) ---
  /**
   * True when `url`/`storageRef` point at an annotated render of the photo. The
   * pristine, un-annotated original is preserved in `originalUrl`/`originalStorageRef`
   * so annotations are non-destructive (originals can be shown or re-annotated).
   */
  hasAnnotation?: boolean;
  /** Public URL of the pristine, un-annotated original (present when `hasAnnotation`). */
  originalUrl?: string;
  /** Storage ref of the pristine original (present when `hasAnnotation`). */
  originalStorageRef?: string;
}

/** A contact collected from a business card or badge photo (or entered manually) */
export interface SurveyContact {
  contactId: string;
  name?: string;
  title?: string;
  company?: string;
  email?: string;
  phone?: string;
  source: 'business_card' | 'badge' | 'manual';
  /** Rep-typed notes about the person (preserved end-to-end — audit A8). */
  notes?: string;
  /** Intake photo this contact was extracted from, if any */
  photoId?: string;
  /** ISO 8601 timestamp */
  capturedAt: string;
  capturedBy: string;
}

/** A ranked lead the AI thinks this survey might belong to */
export interface LeadSuggestion {
  leadId: string;
  companyName: string;
  address?: string;
  /** 0-100 match score */
  score: number;
  /** Human-readable reasons this lead was suggested */
  reasons: string[];
  distanceMiles?: number;
}

export type LeadLinkStatus = 'unlinked' | 'suggested' | 'confirmed' | 'dismissed';

/** AI-polished prose sections for the site visit report */
export interface PolishedReportSections {
  /** One-paragraph executive summary of the visit */
  summary: string;
  /** Client-friendly description of electrical findings */
  electricalFindings?: string;
  /** Client-friendly description of gas service findings */
  gasFindings?: string;
  /** Client-friendly description of the proposed installation site */
  siteConditions?: string;
  /** AI-generated recommended next steps for the customer */
  recommendedNextSteps?: string;
}

// === Note Cards ===

/** A discrete observation captured during a site visit */
export interface SurveyNoteCard {
  cardId: string;
  order: number;
  text: string;
  photoId?: string;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

// === Main Document ===

/** Site survey document stored in the siteSurveys collection */
export interface SiteSurvey {
  surveyId: string;
  orgId: string;
  leadId?: string;
  createdBy: string;
  createdAt: FirestoreTimestampLike;
  updatedAt: FirestoreTimestampLike;
  status: SurveyStatus;
  captureMode: CaptureMode;
  interviewSessionId?: string;

  siteInfo?: SiteInfoSection;
  electricalPanel?: ElectricalPanelSection;
  gasMeter?: GasMeterSection;
  generatorPad?: GeneratorPadSection;
  loads?: LoadsSection;
  existingEquipment?: ExistingEquipmentSection;
  permit?: PermitSection;
  proposal?: ProposalSection;
  siteMap?: SiteMapSection;
  /** Ranked placement options (survey PR 3b), written by survey_savePlacementOptions. */
  placementOptions?: PlacementOption[];
  /** The option chosen for the permit, and who chose it and when (server-set). */
  chosenPlacementOptionId?: string | null;
  chosenBy?: string;
  chosenAt?: unknown;
  /** Server-set by survey_savePlacementOptions; a save names the version it was based on. */
  placementOptionsWriteVersion?: SurveyWriteVersion;

  recommendation?: SurveyRecommendation;
  photoAnalysis?: PhotoAnalysisResult;

  notes?: string;
  noteCards?: SurveyNoteCard[];
  sectionCompletions: Record<SurveySection, boolean>;

  // Quick mobile capture (AI intake)
  captureLocation?: CaptureLocation;
  resolvedAddress?: ResolvedAddress;
  intakePhotos?: IntakePhoto[];
  contacts?: SurveyContact[];
  /** Company / address hints the AI has extracted from photos and cards */
  companyHints?: string[];
  leadSuggestions?: LeadSuggestion[];
  leadLinkStatus?: LeadLinkStatus;

  // Site visit report
  reportPolishedSections?: PolishedReportSections;
  reportUrl?: string;
  reportGeneratedAt?: string;
  reportDraft?: ReportDraft;
}

// === Helpers ===

/**
 * The sections that count toward completion for THIS survey. The proposal section counts only
 * once it is marked complete: it is not asked yet on every survey (and on a server that predates
 * it, cannot be), so it must never lower the percent, or the "N of M sections" count, of a survey
 * that finished every section it could. Marking it complete raises both the numerator and the
 * denominator together. Used by both `computeCompletionPercent` and any "N of M" display so the
 * two never disagree.
 */
export function countedSurveySections(
  completions?: Record<SurveySection, boolean>,
): readonly SurveySection[] {
  return SURVEY_SECTIONS.filter((s) => s !== 'proposal' || completions?.proposal === true);
}

/**
 * Compute completion percentage from section completion map (client-side only, not stored).
 */
export function computeCompletionPercent(completions?: Record<SurveySection, boolean>): number {
  if (!completions) return 0;
  const counted = countedSurveySections(completions);
  const completed = counted.filter((s) => completions[s]).length;
  return Math.round((completed / counted.length) * 100);
}

/** Create an empty section completions map with all sections set to false */
export function createEmptySectionCompletions(): Record<SurveySection, boolean> {
  return Object.fromEntries(SURVEY_SECTIONS.map((s) => [s, false])) as Record<SurveySection, boolean>;
}

// === Site Map Types ===

/**
 * Every map pin type, once. The server's allow-list is this array and the editor's maps are typed by the
 * union derived from it, so the two cannot drift. Never remove one: saved pins carry these names.
 */
export const SITE_MAP_PIN_TYPES = [
  'utility_service', 'electrical_panel', 'gas_meter', 'generator_pad', 'custom',
  // survey PR 3b (permit site layout): where the transfer switch goes, and where new gas pipe starts
  'transfer_switch', 'gas_tie_in',
  // survey fuel slice S2: where the propane tank goes
  'propane_tank',
] as const;
export type SiteMapPinType = (typeof SITE_MAP_PIN_TYPES)[number];
/** How a pin was placed: GPS as captured, GPS then dragged, or tapped on the map. */
export type SiteMapPinSetBy = 'gps' | 'gps_adjusted' | 'map';
export type SiteMapPathType = 'conduit' | 'gas_pipe' | 'trenching' | 'custom';
export type SiteMapBoundaryType = 'property_line' | 'work_area' | 'freehand';

export interface SiteMapPin {
  id: string;
  type: SiteMapPinType;
  position: { lat: number; lng: number };
  label?: string;
  notes?: string;
  setBy?: SiteMapPinSetBy;
  /** GPS accuracy (metres) when the pin came from the phone's location. */
  gpsAccuracyM?: number;
}

/** One stretch of a route, tagged by how it is run; the tape length overrides the map length. */
export interface SiteMapSegment {
  points: { lat: number; lng: number }[];
  kind: 'trench' | 'above_ground';
  measuredDistanceFt: number;
  overrideDistanceFt?: number;
}

export interface SiteMapPath {
  id: string;
  type: SiteMapPathType;
  points: { lat: number; lng: number }[];
  measuredDistanceFt: number;
  overrideDistanceFt?: number;
  label?: string;
  /** Optional per-stretch breakdown (trench vs above ground). The path totals stay as before. */
  segments?: SiteMapSegment[];
  /** Another path's id when both share one trench (its footage counts once). */
  commonTrenchWith?: string;
}

export interface SiteMapBoundary {
  id: string;
  type: SiteMapBoundaryType;
  points: { lat: number; lng: number }[];
  areaSquareFt?: number;
  label?: string;
}

/**
 * One ranked place the generator could go (survey PR 3b). Up to 3 per survey; rank 1
 * is OnPoint's recommendation. Exactly one is chosen for the permit.
 */
export interface PlacementOption {
  id: string;
  rank: number;
  /** The generator_pad pin for this option, on siteMap.pins. */
  pinId: string;
  /** Direction of the unit's long side, degrees clockwise from north. */
  facingDeg: number;
  gasPathId?: string;
  electricalPathId?: string;
  measurements: {
    toWallFt?: number;
    toOpeningFt?: number;
    openingKind?: 'window' | 'door' | 'vent' | 'other';
    toLotLineFt?: number;
  };
  /** Survey photo ids of the wall the unit would sit against. */
  elevationPhotoIds: string[];
  notes?: string;
  /** Server-set when the option's pad pin moved after it was measured: re-check the tape numbers. */
  measurementsNeedCheck?: boolean;
}

export interface SiteMapSection {
  center: { lat: number; lng: number };
  zoom: number;
  pins: SiteMapPin[];
  paths: SiteMapPath[];
  boundaries: SiteMapBoundary[];
  snapshotUrl?: string;
  snapshotStorageRef?: string;
  /** Server-set by survey_saveMap; a save names the version it was based on. */
  writeVersion?: SurveyWriteVersion;
}

/**
 * Version of a whole-replace survey field (site map, placement options), set only by
 * the server. A save sends `baseVersion` (this `version` as it read it, 0 if absent)
 * and is refused when another writer saved after that version. A save that sends no
 * `baseVersion` at all (a client from before versions) is read as base 0, strict.
 */
export interface SurveyWriteVersion {
  version: number;
  /**
   * The tab or caller that made the latest save; null when it sent none. Cooperative,
   * not identity: any caller can send any string. It only lets one tab's own queued
   * saves chain; it is not access control and does not prove who wrote what.
   */
  writerId: string | null;
  /** The version that writer's unbroken run of saves started from. */
  writerSince: number;
}
